import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { fetchTour } from "@/lib/api";
import InquiryForm from "@/components/InquiryForm";
import { ArrowLeft, Check, Clock, MapPin, Users } from "lucide-react";

export default function TourDetail() {
  const { slug } = useParams();
  const [tour, setTour] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    fetchTour(slug)
      .then(setTour)
      .catch(() => setNotFound(true));
  }, [slug]);

  if (notFound) {
    return (
      <div className="max-w-3xl mx-auto px-5 sm:px-8 py-32 text-center" data-testid="tour-not-found">
        <div className="eyebrow text-[var(--clay)] mb-4">Lost in the bog</div>
        <h1 className="font-display text-5xl mb-4">Tour not found.</h1>
        <Link to="/tours" className="btn-ghost mt-6">
          <ArrowLeft size={16} /> Back to tours
        </Link>
      </div>
    );
  }
  if (!tour) {
    return <div className="max-w-7xl mx-auto px-5 sm:px-8 py-32 text-[var(--ink-soft)]">Loading…</div>;
  }

  return (
    <div data-testid={`page-tour-${tour.slug}`}>
      {/* HERO */}
      <section className="relative">
        <div className="aspect-[16/10] sm:aspect-[16/9] w-full overflow-hidden bg-[var(--bg-2)]">
          <img src={tour.image_url} alt={tour.title} className="w-full h-full object-cover object-bottom" />
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-8 -mt-16 sm:-mt-28 relative">
          <div className="bg-[var(--bg)] border border-[var(--border)] p-6 sm:p-12 max-w-3xl">
            <Link to="/tours" className="text-sm text-[var(--ink-soft)] hover:text-[var(--ink)] inline-flex items-center gap-1.5 mb-6" data-testid="tour-back-link">
              <ArrowLeft size={14} /> All tours
            </Link>
            <span className="chip mb-5" data-testid="tour-detail-category">{tour.category}</span>
            <h1 className="font-display text-4xl sm:text-6xl leading-[0.95] mt-2 break-words">{tour.title}</h1>
            <p className="text-[var(--ink-soft)] mt-5 sm:mt-6 leading-relaxed text-base sm:text-lg">{tour.short_description}</p>
          </div>
        </div>
      </section>

      {/* BODY */}
      <section className="max-w-7xl mx-auto px-4 sm:px-8 mt-12 sm:mt-20 grid grid-cols-1 lg:grid-cols-12 gap-10 sm:gap-12 lg:gap-16">
        <div className="lg:col-span-7 space-y-10 sm:space-y-12">
          {/* Quick facts */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-y-6 sm:gap-y-8 gap-x-6 pb-8 sm:pb-10 border-b border-[var(--border)]">
            <Fact icon={<Clock size={18} />} label="Duration" value={tour.duration} />
            <Fact icon={<Users size={18} />} label="Group" value={tour.group_size} />
            <Fact icon={<MapPin size={18} />} label="Where" value={tour.location} />
            <Fact label="From" value={tour.price_from ? `€${tour.price_from} per person` : "On request"} />
          </div>

          {/* Description */}
          <div>
            <div className="eyebrow text-[var(--pine)] mb-4">About this tour</div>
            <p className="text-lg leading-relaxed text-[var(--ink)]">{tour.long_description}</p>
          </div>

          {/* Highlights */}
          <div>
            <div className="eyebrow text-[var(--pine)] mb-4">Highlights</div>
            <ul className="space-y-3">
              {tour.highlights.map((h, i) => (
                <li key={i} className="flex items-start gap-3 text-[var(--ink)]">
                  <Check size={18} className="mt-0.5 text-[var(--pine)] shrink-0" />
                  <span>{h}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Gallery */}
          {tour.gallery && tour.gallery.length > 1 && (
            <div data-testid="tour-gallery">
              <div className="eyebrow text-[var(--pine)] mb-4">See it</div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {tour.gallery.slice(1).map((src, i) => (
                  <a
                    key={i}
                    href={src}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block aspect-[4/3] overflow-hidden bg-[var(--bg-2)] group"
                    data-testid={`gallery-image-${i}`}
                  >
                    <img
                      src={src}
                      alt={`${tour.title} — photo ${i + 2}`}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.03]"
                      loading="lazy"
                    />
                  </a>
                ))}
              </div>
            </div>
          )}

          {/* Pricing tiers */}
          {tour.pricing_tiers && tour.pricing_tiers.length > 0 && (
            <div data-testid="tour-pricing-table">
              <div className="eyebrow text-[var(--pine)] mb-4">Pricing</div>
              {tour.pricing_note && (
                <p className="text-[var(--ink-soft)] text-sm mb-5 max-w-lg">
                  {tour.pricing_note}
                </p>
              )}
              <div className="border border-[var(--border)] divide-y divide-[var(--border)]">
                {tour.pricing_tiers.map((t, i) => (
                  <div key={i} className="flex items-baseline justify-between px-5 py-4">
                    <span className="text-[var(--ink)] font-medium">{t.label}</span>
                    <span className="font-display text-xl">
                      €{t.price}
                      <span className="text-[var(--ink-soft)] text-sm font-normal ml-1.5">{t.unit}</span>
                    </span>
                  </div>
                ))}
              </div>
              <p className="text-[var(--ink-soft)] text-xs mt-3">
                Final price depends on duration and add-ons. Send an inquiry for an exact quote.
              </p>
            </div>
          )}
        </div>

        {/* Inquiry */}
        <div className="lg:col-span-5">
          <div className="lg:sticky lg:top-28 space-y-6">
            <div className="border border-[var(--border)] bg-white p-6 sm:p-8">
              <div className="eyebrow text-[var(--pine)] mb-2">Inquire</div>
              <h3 className="font-display text-3xl mb-2">Plan this one.</h3>
              <p className="text-[var(--ink-soft)] text-sm mb-6">
                Pricing depends on group size and season. Send us a quick note and we'll come back with a quote.
              </p>
              <InquiryForm tour={tour} />
            </div>
            <div className="bg-[var(--bg-2)] p-6 sm:p-7" data-testid="booking-info">
              <div className="eyebrow text-[var(--pine)] mb-3">How booking works</div>
              <p className="text-[var(--ink)] text-sm leading-relaxed">
                Submit an inquiry and we'll come back with a quote. Payment is made on the day, by bank transfer or cash, whichever suits your group.
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

function Fact({ icon, label, value }) {
  return (
    <div>
      <div className="eyebrow text-[var(--ink-soft)] mb-2 flex items-center gap-1.5">{icon}{label}</div>
      <div className="font-display-light text-base">{value}</div>
    </div>
  );
}
